import React, { useEffect, useState } from 'react';
import type { SiteLayoutData } from '../types';
import { Lock, Unlock } from 'lucide-react';

interface FooterProps {
  isAdmin?: boolean;
  onOpenAdminLogin?: () => void;
  onLogoutAdmin?: () => void;
}

export const Footer: React.FC<FooterProps> = ({
  isAdmin,
  onOpenAdminLogin,
  onLogoutAdmin,
}) => {
  const [layout, setLayout] = useState<SiteLayoutData | null>(null);

  useEffect(() => {
    fetch('/api/layout')
      .then((r) => (r.ok ? (r.json() as Promise<SiteLayoutData>) : null))
      .then((data) => {
        if (data) setLayout(data);
      })
      .catch((e) => console.warn('Could not load footer layout:', e));
  }, []);

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="site-footer mt-auto py-5">
      <div className="container">
        <div className="row gy-4">
          <div className="col-lg-5">
            <div className="d-flex align-items-center gap-2 mb-3">
              <img src="/img/favicons/favicon.png" alt="Logo" width="28" height="28" />
              <h5 className="mb-0 fw-bold">{layout?.header.siteTitle || 'Echocondria'}</h5>
            </div>
            <p className="small mb-3">
              {layout?.footer.aboutText || 'An interactive tabletop RPG world, comprehensive compendium, atlas, and rules engine.'}
            </p>
            <div className="d-flex align-items-center gap-3">
              <p className="small mb-0 opacity-75">
                {layout?.footer.copyrightText || `© ${currentYear} Echocondria. All rights reserved.`}
              </p>
              <button
                type="button"
                className="btn btn-link btn-sm text-secondary p-0 text-decoration-none opacity-50 hover-opacity-100"
                onClick={isAdmin ? onLogoutAdmin : onOpenAdminLogin}
                title={isAdmin ? 'Admin Mode Active (Click to Logout)' : 'Admin Login'}
                aria-label={isAdmin ? 'Admin Mode Active (Click to Logout)' : 'Admin Login'}
                style={{ lineHeight: 1 }}
              >
                {isAdmin ? <Unlock size={13} className="text-warning" /> : <Lock size={12} />}
              </button>
            </div>
          </div>

          <div className="col-6 col-md-3 col-lg-2 offset-lg-1">
            <h6 className="fw-semibold mb-3">Rules & Lore</h6>
            <ul className="list-unstyled small d-flex flex-column gap-2 mb-0">
              <li><a href="/classes" className="footer-nav-link" onClick={(e) => navigateTo(e, '/classes')}>Classes</a></li>
              <li><a href="/races" className="footer-nav-link" onClick={(e) => navigateTo(e, '/races')}>Races</a></li>
              <li><a href="/skills" className="footer-nav-link" onClick={(e) => navigateTo(e, '/skills')}>Skills</a></li>
              <li><a href="/abilities" className="footer-nav-link" onClick={(e) => navigateTo(e, '/abilities')}>Abilities</a></li>
              <li><a href="/backgrounds" className="footer-nav-link" onClick={(e) => navigateTo(e, '/backgrounds')}>Backgrounds</a></li>
            </ul>
          </div>

          <div className="col-6 col-md-3 col-lg-2">
            <h6 className="fw-semibold mb-3">Compendium</h6>
            <ul className="list-unstyled small d-flex flex-column gap-2 mb-0">
              <li><a href="/items" className="footer-nav-link" onClick={(e) => navigateTo(e, '/items')}>Items & Gear</a></li>
              <li><a href="/enemies" className="footer-nav-link" onClick={(e) => navigateTo(e, '/enemies')}>Bestiary / Enemies</a></li>
              <li><a href="/npcs" className="footer-nav-link" onClick={(e) => navigateTo(e, '/npcs')}>NPCs</a></li>
              <li><a href="/quests" className="footer-nav-link" onClick={(e) => navigateTo(e, '/quests')}>Quests</a></li>
              <li><a href="/lexicon" className="footer-nav-link" onClick={(e) => navigateTo(e, '/lexicon')}>Lexicon</a></li>
            </ul>
          </div>

          <div className="col-12 col-md-4 col-lg-2">
            <h6 className="fw-semibold mb-3">World</h6>
            <ul className="list-unstyled small d-flex flex-column gap-2 mb-0">
              <li><a href="/maps" className="footer-nav-link" onClick={(e) => navigateTo(e, '/maps')}>City & World Maps</a></li>
              <li><a href="/cities" className="footer-nav-link" onClick={(e) => navigateTo(e, '/cities')}>Cities</a></li>
              <li><a href="/city-venues" className="footer-nav-link" onClick={(e) => navigateTo(e, '/city-venues')}>Venues</a></li>
              <li><a href="/organizations" className="footer-nav-link" onClick={(e) => navigateTo(e, '/organizations')}>Organizations</a></li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};
